<!-- Полноэкранный последовательный мастер приёмки и передачи ТС по snapshot шагов. -->
<template>
  <BaseModal
    :visible="visible"
    size="fullscreen"
    body-padding="none"
    adaptive-fullscreen
    :close-on-overlay="false"
    @close="$emit('close')"
  >
    <template #header>
      <base-button
        v-if="inspection && !done && !resumePrompt && stepIndex > 0"
        class="inspection-wizard__nav-button"
        variant="ghost"
        icon="chevronLeft"
        :icon-size="18"
        aria-label="Предыдущий этап"
        :disabled="store.isSubmitting"
        @click="goBack"
      />
      <span v-else class="inspection-wizard__nav-placeholder"></span>
      <div v-if="inspection && !done && !resumePrompt" class="inspection-wizard__progress">
        <span>{{ `${stepIndex + 1} из ${steps.length}` }}</span>
        <div><i :style="{ width: progressWidth }"></i></div>
      </div>
      <span v-else class="inspection-wizard__progress"></span>
    </template>

    <base-loader v-if="store.dataIsLoading || store.draftIsLoading" />

    <div v-else-if="done" class="inspection-wizard__done">
      <span class="inspection-wizard__done-icon"><AppIcon name="check" :size="48" /></span>
      <h1>Готово</h1>
      <p>Акт отправлен участникам на подпись</p>
      <base-primary-button size="large" @click="$emit('close')">Закрыть</base-primary-button>
    </div>

    <VehicleInspectionStepLayout
      v-else-if="resumePrompt && inspection"
      class="inspection-wizard__resume"
      title="У вас есть незаконченный акт"
      :description="resumeDescription"
    >
      <div v-if="resumePhotoItems.length" class="inspection-wizard__resume-photos">
        <figure v-for="item in resumePhotoItems" :key="item.step.code">
          <img :src="attachmentPreviewUrl(item.attachment)" :alt="item.step.name" />
          <figcaption>{{ item.step.name }}</figcaption>
        </figure>
      </div>
      <template #actions>
        <base-primary-button size="large" :disabled="store.isSubmitting" @click="continueDraft">Продолжить</base-primary-button>
      </template>
    </VehicleInspectionStepLayout>

    <VehicleInspectionStepLayout v-else-if="!inspection" class="inspection-wizard__start" centered>
      <template #heading>
        <VehicleIllustration code="left" />
        <strong>{{ vehicle.full_name || vehicle.registration_number }}</strong>
        <h1>Что вы делаете?</h1>
      </template>
      <template #actions>
        <base-primary-button size="large" :disabled="store.isSubmitting" @click="start('receiver')">Принимаю машину</base-primary-button>
        <base-button size="large" :disabled="store.isSubmitting" @click="start('giver')">Сдаю машину</base-button>
      </template>
    </VehicleInspectionStepLayout>

    <component
      v-else-if="currentStepComponent"
      :is="currentStepComponent"
      :key="currentStep.code"
      :step="currentStep"
      :answer="currentAnswer"
      :inspection="inspection"
      :vehicle-id="vehicle.id"
      :disabled="store.isSubmitting"
      @complete="completeStep"
      @open-remarks="openRemarks"
    />
  </BaseModal>

  <VehicleRemarkDetailsModal
    :visible="Boolean(selectedRemark)"
    :remark="selectedRemark"
    readonly
    @close="selectedRemarkId = null"
  />
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import VehicleIllustration from "@/components/Vehicle/Inspection/VehicleIllustration.vue";
import PhotoStep from "@/components/Vehicle/Inspection/Step/Photo.vue";
import RemarksStep from "@/components/Vehicle/Inspection/Step/Remarks.vue";
import SignatureStep from "@/components/Vehicle/Inspection/Step/Signature.vue";
import QuestionStep from "@/components/Vehicle/Inspection/Step/Question.vue";
import NumberStep from "@/components/Vehicle/Inspection/Step/Number.vue";
import TextStep from "@/components/Vehicle/Inspection/Step/Text.vue";
import VehicleInspectionStepLayout from "@/components/Vehicle/Inspection/Step/Layout.vue";
import VehicleRemarkDetailsModal from "@/components/Vehicle/Remark/Details/Modal.vue";
import { useAttachmentStore } from "@/stores/attachmentStore";
import { useFleetVehicleInspectionStore } from "@/stores/fleetVehicleInspectionStore";
import { useFleetVehicleRemarkStore } from "@/stores/fleetVehicleRemarkStore";
import { formatDateAndTimeLocal } from "@/utils/dateUtils";
import { attachmentPreviewUrl } from "@/utils/fileUtils";

const STEP_COMPONENTS = {
  photo: PhotoStep,
  remarks: RemarksStep,
  signature: SignatureStep,
  question: QuestionStep,
  number: NumberStep,
  text: TextStep,
};

export default {
  name: "VehicleInspectionWizard",
  components: { AppIcon, VehicleIllustration, VehicleInspectionStepLayout, VehicleRemarkDetailsModal },
  props: {
    visible: { type: Boolean, required: true },
    vehicle: { type: Object, required: true },
    waybillId: { type: [Number, String], default: null },
    draftId: { type: [Number, String], default: null },
  },
  emits: ["close", "completed"],
  data() {
    return {
      stepIndex: 0,
      done: false,
      pendingSignature: null,
      resumePrompt: false,
      selectedRemarkId: null,
      draftLoadRequest: 0,
    };
  },
  computed: {
    attachmentStore() { return useAttachmentStore(); },
    store() { return useFleetVehicleInspectionStore(); },
    remarkStore() { return useFleetVehicleRemarkStore(); },
    inspection() { return this.store.currentInspection; },
    steps() { return this.inspection?.steps_snapshot || []; },
    currentStep() { return this.steps[this.stepIndex] || null; },
    currentStepComponent() { return STEP_COMPONENTS[this.currentStep?.field_type] || null; },
    currentAnswer() { return this.inspection?.answers?.find((answer) => answer.step_code === this.currentStep?.code) || null; },
    selectedRemark() {
      return this.remarkStore.remarks.find((remark) => String(remark.id) === String(this.selectedRemarkId)) || null;
    },
    progressWidth() { return `${((this.stepIndex + 1) / Math.max(this.steps.length, 1)) * 100}%`; },
    resumeParticipant() {
      return (this.inspection?.participants || []).find((participant) => String(participant.user_id) === String(this.inspection?.initiator_id));
    },
    resumeDescription() {
      const side = this.resumeParticipant?.participation_role === "receiver" ? "Принимаю машину" : "Сдаю машину";
      const answered = new Set((this.inspection?.answers || []).map((answer) => answer.step_code));
      return `Начат ${formatDateAndTimeLocal(this.inspection.created_at)} · ${side} · сделано ${answered.size} шагов из ${this.steps.length}`;
    },
    resumePhotoItems() {
      return this.steps.filter((step) => step.field_type === "photo").map((step) => ({
        step,
        attachment: this.inspection?.answers?.find((answer) => answer.step_code === step.code)?.attachments?.[0],
      })).filter((item) => item.attachment);
    },
  },
  watch: {
    visible: {
      immediate: true,
      async handler(value) {
        const request = ++this.draftLoadRequest;
        this.selectedRemarkId = null;
        if (!value) return;
        this.done = false;
        this.pendingSignature = null;
        const vehicleId = this.vehicle.id;
        const draftId = this.draftId;
        const draft = await this.store.resumeDraft(vehicleId, draftId);
        // Закрытый или переключённый мастер не должен менять состояние нового сценария.
        if (request !== this.draftLoadRequest || !this.visible ||
            String(vehicleId) !== String(this.vehicle.id) || String(draftId) !== String(this.draftId)) return;
        this.resumePrompt = Boolean(draft) && this.draftId == null;
        if (!this.resumePrompt) this.restoreProgress();
      },
    },
  },
  methods: {
    async start(side) {
      await this.store.createInspection(this.vehicle.id, side, this.waybillId);
      this.pendingSignature = null;
      this.resumePrompt = false;
      this.stepIndex = 0;
    },
    continueDraft() {
      this.resumePrompt = false;
      this.restoreProgress();
    },
    restoreProgress() {
      if (!this.inspection) { this.stepIndex = 0; return; }
      const answered = new Set((this.inspection.answers || []).map((answer) => answer.step_code));
      const firstPending = this.steps.findIndex((step) => step.field_type === "signature" || !answered.has(step.code));
      this.stepIndex = firstPending === -1 ? Math.max(this.steps.length - 1, 0) : firstPending;
    },
    goBack() { if (this.stepIndex > 0) this.stepIndex -= 1; },
    attachmentPreviewUrl,
    async openRemarks(remarkId = null) {
      if (remarkId != null) {
        await this.remarkStore.fetchRemark(this.vehicle.id, remarkId);
        this.selectedRemarkId = remarkId;
        return;
      }
      const query = { tab: "remarks" };
      await this.$router.push({ name: "VehicleDetails", params: { id: this.vehicle.id }, query });
      this.$emit("close");
    },
    async completeStep(payload) {
      if (this.currentStep.field_type === "signature") {
        const attachment = await this.attachmentStore.uploadAttachment(payload.file, `inspection-${this.inspection.id}-signature.png`);
        this.pendingSignature = { attachmentId: attachment.id };
      } else {
        const answerPayload = { step_code: this.currentStep.code, value: payload.value || {} };
        if (payload.file) {
          const attachment = await this.attachmentStore.uploadAttachment(payload.file, payload.file.name);
          answerPayload.attachment_ids = [attachment.id];
        } else if (payload.attachmentIds) {
          answerPayload.attachment_ids = payload.attachmentIds;
        }
        await this.store.saveAnswer(this.vehicle.id, this.inspection.id, answerPayload);
      }

      if (this.stepIndex < this.steps.length - 1) {
        this.stepIndex += 1;
        return;
      }

      if (!this.pendingSignature) {
        const signatureIndex = this.steps.findIndex((step) => step.field_type === "signature");
        if (signatureIndex >= 0) this.stepIndex = signatureIndex;
        return;
      }

      await this.store.completeInspection(
        this.vehicle.id,
        this.inspection.id,
        [this.pendingSignature.attachmentId]
      );
      this.done = true;
      this.$emit("completed", this.store.currentInspection);
    },
  },
  beforeUnmount() {
    this.draftLoadRequest += 1;
    this.store.resetCurrent();
  },
};
</script>

<style scoped>
.inspection-wizard__nav-button {
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid var(--color-grey);
  border-radius: 8px;
  background: var(--color-light);
  color: var(--color-dark);
}

.inspection-wizard__nav-button:disabled {
  background: color-mix(in srgb, var(--color-light) 94%, var(--color-grey));
  color: var(--color-grey);
  cursor: default;
}

.inspection-wizard__nav-placeholder {
  width: 40px;
}

.inspection-wizard__progress {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
}

.inspection-wizard__progress div {
  width: 100%;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  background: var(--color-grey);
}

.inspection-wizard__progress i {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: var(--color-green);
  transition: width .2s ease;
}

.inspection-wizard__start, .inspection-wizard__done {
  min-height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  text-align: center;
}

.inspection-wizard__start h1, .inspection-wizard__done h1 {
  margin: 0;
  font-size: 26px;
}

.inspection-wizard__start :deep(.inspection-step__actions), .inspection-wizard__done .btn {
  width: min(100%, 520px);
}

.inspection-wizard__done p {
  margin: -12px 0 20px;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
}

.inspection-wizard__done-icon {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--color-green) 10%, var(--color-light));
  color: color-mix(in srgb, var(--color-green) 55%, var(--color-dark));
}

.inspection-wizard__resume-photos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.inspection-wizard__resume-photos figure {
  margin: 0;
}

.inspection-wizard__resume-photos img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 8px;
}

.inspection-wizard__resume-photos figcaption {
  margin-top: 4px;
  font-size: 12px;
  color: color-mix(in srgb, var(--color-dark) 65%, var(--color-light));
}
</style>
